/**
 * Clipboard utilities for copying content with HTML table support.
 * When content contains markdown tables, copies as HTML so tables
 * render with proper alignment in any paste target (rich text editors,
 * Google Docs, Notion, etc.). Falls back to plain text for non-rich targets.
 */

const esc = (str) => (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Convert markdown content to HTML, preserving tables as <table> elements.
 * Other markdown is converted to basic HTML equivalents.
 */
export const markdownToHtml = (markdown) => {
  if (!markdown) return '';

  const lines = markdown.split('\n');
  let html = '';
  let inTable = false;
  let inCodeBlock = false;
  let codeContent = '';
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Handle code blocks
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        html += `<pre><code>${esc(codeContent.trim())}</code></pre>`;
        codeContent = '';
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }
    if (inCodeBlock) {
      codeContent += line + '\n';
      continue;
    }

    // Detect markdown table rows (contains | and is not a separator line like |---|)
    const isTableRow = /^\|(.+)\|$/.test(line.trim());
    const isSeparator = /^\|[\s\-:|]+\|$/.test(line.trim());

    if (isTableRow || isSeparator) {
      if (!inTable) {
        if (inList) { html += '</ul>'; inList = false; }
        html += '<table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse; margin:8px 0;">';
        inTable = true;
      }
      if (isSeparator) continue; // Skip --- separator rows

      const cells = line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
      // First data row after table start = header
      const isHeader = i + 1 < lines.length && /^\|[\s\-:|]+\|$/.test(lines[i + 1].trim());
      const tag = isHeader ? 'th' : 'td';
      html += '<tr>';
      cells.forEach(cell => {
        html += `<${tag}>${esc(stripInlineMarkdown(cell))}</${tag}>`;
      });
      html += '</tr>';
      continue;
    }

    // Close table if we were in one
    if (inTable) {
      html += '</table>';
      inTable = false;
    }

    // Empty line
    if (line.trim() === '') {
      if (inList) { html += '</ul>'; inList = false; }
      continue;
    }

    // Headings
    const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
    if (headingMatch) {
      if (inList) { html += '</ul>'; inList = false; }
      const level = headingMatch[1].length;
      html += `<h${level}>${esc(stripInlineMarkdown(headingMatch[2]))}</h${level}>`;
      continue;
    }

    // List items
    const listMatch = line.match(/^\s*[-*+]\s+(.+)/);
    if (listMatch) {
      if (!inList) { html += '<ul>'; inList = true; }
      html += `<li>${esc(stripInlineMarkdown(listMatch[1]))}</li>`;
      continue;
    }

    // Regular paragraph
    if (inList) { html += '</ul>'; inList = false; }
    html += `<p>${esc(stripInlineMarkdown(line))}</p>`;
  }

  if (inTable) html += '</table>';
  if (inList) html += '</ul>';
  if (inCodeBlock) html += `<pre><code>${esc(codeContent.trim())}</code></pre>`;

  return html;
};

/**
 * Strip inline markdown formatting from text (bold, italic, code, links).
 * Used for plain text output.
 */
export const stripInlineMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
};

/**
 * Strip all markdown formatting for plain text clipboard.
 */
export const stripMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`{3}[\s\S]*?`{3}/g, '')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '• ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^---+$/gm, '');
};

/**
 * Copy content to clipboard with both HTML and plain text formats.
 * If content contains markdown tables, the HTML version preserves table alignment.
 */
export const copyWithHtml = async (markdown, plainText) => {
  const html = markdownToHtml(markdown);
  const text = plainText || stripMarkdown(markdown);

  await navigator.clipboard.write([
    new ClipboardItem({
      'text/html': new Blob([html], { type: 'text/html' }),
      'text/plain': new Blob([text], { type: 'text/plain' })
    })
  ]);
};
